import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Field } from "@/components/partners/common";
import { KIND, titleCase, apiError, fieldErrors } from "@/lib/partnerApi";
import { CURRENCIES, currencySymbol } from "@/lib/currency";

export const selectCls = "h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50";

export function Sel({ value, onChange, options, placeholder = "—", tid, disabled }) {
  return (
    <select className={selectCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} disabled={disabled}>
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const v = typeof o === "object" ? o.value : o;
        const l = typeof o === "object" ? o.label : titleCase(o);
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  );
}

const EMPTY = {
  name: "", legal_name: "", short_name: "", partner_type: "", category: "", priority: "medium",
  registration_date: "", website: "", parent_id: "", rating: "", territory: "", sales_zone: "",
  assigned_salesperson: "", assigned_buyer: "", price_level: "", notes: "",
  tax_registration_country: "India", gst_registration_type: "", gstin: "", pan: "", tan: "", cin: "",
  msme_number: "", udyam_number: "", tds_applicable: false, tds_category: "", tax_category: "",
  tax_identification_number: "", tax_exemption_details: "", custom_fields: {},
  commercial: { payment_terms: "", currency: "INR" },
};
const EMPTY_CONTACT = { contact_role: "", name: "", designation: "", mobile: "", email: "" };
const EMPTY_ADDR = { address_type: "", line1: "", line2: "", area: "", city: "", state: "", country: "India", pin: "" };

/** Create / edit the master record. `initial` = existing partner for edit. */
export default function PartnerForm({ kind, meta, open, onOpenChange, initial, partners = [], onSaved }) {
  const editing = !!initial;
  const [f, setF] = useState(EMPTY);
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [errors, setErrors] = useState({});
  const [dups, setDups] = useState(null);
  const [dupReason, setDupReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({}); setDups(null); setDupReason("");
    if (initial) {
      setF({ ...EMPTY, ...initial, commercial: { ...EMPTY.commercial, ...(initial.commercial || {}) },
        custom_fields: initial.custom_fields || {} });
    } else {
      setF({ ...EMPTY, commercial: { ...EMPTY.commercial } });
      setContact({ ...EMPTY_CONTACT, contact_role: meta?.contact_roles?.[0] || "" });
      setAddr({ ...EMPTY_ADDR, address_type: meta?.address_types?.[0] || "" });
    }
  }, [open, initial, meta]);

  if (!meta) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setC = (k, v) => setF((p) => ({ ...p, commercial: { ...p.commercial, [k]: v } }));
  const setCF = (k, v) => setF((p) => ({ ...p, custom_fields: { ...p.custom_fields, [k]: v } }));
  const can = (a) => (meta.my_actions || []).includes(a);
  const approvedRecord = editing && initial?.approval_status === "approved";
  const lockCredit = approvedRecord && !can("change_credit_limit");
  const lockPricing = approvedRecord && !can("change_pricing");
  const isSup = kind === "supplier";
  const users = (meta.users || []).map((u) => ({ value: u.email, label: `${u.name || u.email} (${titleCase(u.role)})` }));
  const err = (k) => errors[k];

  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const build = () => {
    const c = { ...f.commercial };
    ["credit_limit", "credit_days", "discount_pct", "special_discount_pct", "rebate_pct", "commission_pct", "moq",
      "lead_time_days", "standard_delivery_days", "payment_due_days", "early_payment_discount_pct", "late_payment_charge_pct"]
      .forEach((k) => { if (k in c) c[k] = num(c[k]); });
    Object.keys(c).forEach((k) => { if (c[k] === "" ) c[k] = null; });
    const body = { ...f, commercial: c, rating: num(f.rating) };
    // Empty string (not null) so clearing a field on edit actually clears it.
    ["parent_id", "assigned_salesperson", "assigned_buyer", "price_level", "priority", "gst_registration_type", "registration_date"]
      .forEach((k) => { if (!body[k]) body[k] = ""; });
    ["id", "code", "master_status", "approval_status", "created_at", "created_by", "updated_at", "updated_by", "performance",
      "status", "financials_hidden", "name_key", "pm_managed"].forEach((k) => delete body[k]);
    if (!editing) {
      if (contact.name) body.contacts = [{ ...contact, is_primary: true }];
      if (addr.line1) body.addresses = [{ ...addr, is_primary: true }];
    }
    return body;
  };

  const save = async (allowDup = false) => {
    if (busy) return;
    if (!f.name.trim() || !f.partner_type) { toast.error("Name and type are required"); return; }
    setBusy(true); setErrors({});
    try {
      const body = build();
      if (allowDup) { body.allow_duplicate = true; body.duplicate_reason = dupReason; }
      const { data } = editing
        ? await api.put(`${KIND[kind].api}/${initial.id}`, body)
        : await api.post(KIND[kind].api, body);
      toast.success(editing ? `${KIND[kind].label} updated` : `${KIND[kind].label} ${data.code} created as draft`);
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.detail?.duplicates) {
        setDups(e.response.data.detail.duplicates);
      } else {
        setErrors(fieldErrors(e));
        toast.error(apiError(e, "Save failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  const txt = (k, label, props = {}) => (
    <Field label={label} error={err(k)} required={props.required}>
      <Input value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`pf-${k}`} {...props} />
    </Field>
  );
  const ctxt = (k, label, props = {}) => (
    <Field label={label} error={err(`commercial.${k}`)}>
      <Input value={f.commercial?.[k] ?? ""} onChange={(e) => setC(k, e.target.value)} data-testid={`pf-c-${k}`} {...props} />
    </Field>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="partner-form">
        <SheetHeader>
          <SheetTitle>{editing ? `Edit ${KIND[kind].label} ${initial.code}` : `New ${KIND[kind].label}`}</SheetTitle>
          {!editing && <p className="text-xs text-slate-500">Code is auto-generated. The record starts as a draft; complete it and submit for approval from its profile.</p>}
        </SheetHeader>
        <div className="mt-5 space-y-6">
          <section>
            <div className="text-sm font-semibold text-slate-700 mb-2">Basic Information</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {txt("name", `${KIND[kind].label} Name`, { required: true })}
              {txt("legal_name", "Legal Company Name")}
              {txt("short_name", "Short Name")}
              <Field label={`${KIND[kind].label} Type`} required error={err("partner_type")}>
                <Sel value={f.partner_type} onChange={(v) => set("partner_type", v)} options={meta.partner_types} tid="pf-partner_type" />
              </Field>
              {txt("category", "Category")}
              <Field label="Priority"><Sel value={f.priority} onChange={(v) => set("priority", v)} options={meta.priorities} /></Field>
              {txt("registration_date", "Registration Date", { type: "date" })}
              {txt("website", "Website", { placeholder: "https://" })}
              <Field label={isSup ? "Parent Company" : "Parent Dealer"}>
                <Sel value={f.parent_id} onChange={(v) => set("parent_id", v)}
                  options={partners.filter((p) => p.id !== initial?.id).map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))} />
              </Field>
              {isSup ? (
                <>
                  {txt("rating", "Supplier Rating (0–5)", { type: "number", step: "0.1", min: 0, max: 5 })}
                  <Field label="Assigned Buyer" error={err("assigned_buyer")}>
                    <Sel value={f.assigned_buyer} onChange={(v) => set("assigned_buyer", v)} options={users} />
                  </Field>
                </>
              ) : (
                <>
                  {txt("territory", "Territory / Region")}
                  {txt("sales_zone", "Sales Zone")}
                  <Field label="Salesperson Assigned" error={err("assigned_salesperson")}>
                    <Sel value={f.assigned_salesperson} onChange={(v) => set("assigned_salesperson", v)} options={users} tid="pf-salesperson" />
                  </Field>
                  <Field label="Pricing Level" error={err("price_level")}>
                    <Sel value={f.price_level} onChange={(v) => set("price_level", v)} options={meta.price_levels} disabled={lockPricing} />
                  </Field>
                </>
              )}
            </div>
          </section>

          <section>
            <div className="text-sm font-semibold text-slate-700 mb-2">Tax & Statutory</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {txt("tax_registration_country", "Tax Registration Country")}
              <Field label="GST Registration Type" error={err("gst_registration_type")}>
                <Sel value={f.gst_registration_type} onChange={(v) => set("gst_registration_type", v)} options={meta.gst_registration_types} tid="pf-gst-type" />
              </Field>
              {txt("gstin", "GSTIN", { placeholder: "27AAACH1234F1Z5", className: "uppercase" })}
              {txt("pan", "PAN", { placeholder: "AAACH1234F", className: "uppercase" })}
              {txt("tan", "TAN")}
              {txt("cin", "CIN")}
              {txt("msme_number", "MSME Registration No.")}
              {txt("udyam_number", "Udyam Registration No.", { placeholder: "UDYAM-MH-00-0000000" })}
              {isSup ? (
                <>
                  <Field label="TDS Applicable">
                    <Sel value={f.tds_applicable ? "yes" : "no"} onChange={(v) => set("tds_applicable", v === "yes")}
                      options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
                  </Field>
                  {txt("tds_category", "TDS Category / Section", { placeholder: "194C" })}
                </>
              ) : txt("tax_category", "Tax Category")}
              {txt("tax_identification_number", "Tax ID (non-India)")}
              <div className="col-span-2 md:col-span-3">{txt("tax_exemption_details", "Tax Exemption Details")}</div>
            </div>
          </section>

          <section>
            <div className="text-sm font-semibold text-slate-700 mb-2">Commercial Terms</div>
            {(lockCredit || lockPricing) && (
              <p className="text-[11px] text-amber-700 mb-2">Credit / pricing fields on an approved record need the Change Credit Limit / Change Pricing permission.</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Payment Terms" error={err("commercial.payment_terms")}>
                <Sel value={f.commercial.payment_terms} onChange={(v) => setC("payment_terms", v)}
                  options={meta.payment_terms.map((p) => ({ value: p.code, label: p.label }))} tid="pf-payment-terms" />
              </Field>
              {f.commercial.payment_terms === "custom" && ctxt("payment_due_days", "Payment Due Days", { type: "number" })}
              {ctxt("credit_limit", `Credit Limit (${currencySymbol(f.commercial?.currency)})`, { type: "number", disabled: lockCredit })}
              {!isSup && ctxt("credit_days", "Credit Days", { type: "number", disabled: lockCredit })}
              <Field label="Currency" error={err("commercial.currency")}>
                <Sel value={f.commercial?.currency || "INR"} onChange={(v) => setC("currency", v || "INR")}
                  options={CURRENCIES.map((c) => ({ value: c.code, label: c.label }))} tid="pf-c-currency" />
              </Field>
              {ctxt("price_list", isSup ? "Price List" : "Dealer Price List", { disabled: lockPricing })}
              {ctxt("discount_pct", "Discount %", { type: "number", disabled: lockPricing })}
              {!isSup && (
                <>
                  {ctxt("special_discount_pct", "Special Discount %", { type: "number", disabled: lockPricing })}
                  {ctxt("scheme", "Scheme", { disabled: lockPricing })}
                  {ctxt("rebate_pct", "Rebate %", { type: "number", disabled: lockPricing })}
                  {ctxt("commission_pct", "Commission %", { type: "number", disabled: lockPricing })}
                  {ctxt("effective_date", "Effective Date", { type: "date" })}
                </>
              )}
              {isSup && ctxt("tax_terms", "Tax Terms")}
              {ctxt("freight_terms", "Freight Terms")}
              {ctxt("delivery_terms", "Delivery Terms")}
              {isSup && ctxt("incoterms", "Incoterms", { placeholder: "FOB / CIF / EXW" })}
              {ctxt("moq", "Minimum Order Qty", { type: "number" })}
              {isSup && (
                <>
                  {ctxt("lead_time_days", "Lead Time (days)", { type: "number" })}
                  {ctxt("standard_delivery_days", "Standard Delivery (days)", { type: "number" })}
                  {ctxt("early_payment_discount_pct", "Early Payment Discount %", { type: "number" })}
                  {ctxt("late_payment_charge_pct", "Late Payment Charges %", { type: "number" })}
                </>
              )}
              {ctxt("agreement_number", isSup ? "Purchase Agreement No." : "Dealer Agreement No.")}
              {ctxt("agreement_start", "Agreement Start", { type: "date" })}
              {ctxt("agreement_end", "Agreement End", { type: "date" })}
            </div>
          </section>

          {!editing && (
            <section>
              <div className="text-sm font-semibold text-slate-700 mb-2">Primary Contact & Address <span className="text-xs font-normal text-slate-400">(more can be added on the profile)</span></div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Contact Role"><Sel value={contact.contact_role} onChange={(v) => setContact({ ...contact, contact_role: v })} options={meta.contact_roles} /></Field>
                <Field label="Contact Person" error={err("contacts[0].name")}><Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="pf-contact-name" /></Field>
                <Field label="Designation"><Input value={contact.designation} onChange={(e) => setContact({ ...contact, designation: e.target.value })} /></Field>
                <Field label="Mobile" error={err("contacts[0].mobile")}><Input value={contact.mobile} onChange={(e) => setContact({ ...contact, mobile: e.target.value })} data-testid="pf-contact-mobile" /></Field>
                <Field label="Email" error={err("contacts[0].email")}><Input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="pf-contact-email" /></Field>
                <div />
                <Field label="Address Type"><Sel value={addr.address_type} onChange={(v) => setAddr({ ...addr, address_type: v })} options={meta.address_types} /></Field>
                <Field label="Address Line 1" error={err("addresses[0].line1")} className="md:col-span-2"><Input value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="pf-addr-line1" /></Field>
                <Field label="Address Line 2"><Input value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} /></Field>
                <Field label="Area"><Input value={addr.area} onChange={(e) => setAddr({ ...addr, area: e.target.value })} /></Field>
                <Field label="City" error={err("addresses[0].city")}><Input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="pf-addr-city" /></Field>
                <Field label="State" error={err("addresses[0].state")}><Input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} data-testid="pf-addr-state" /></Field>
                <Field label="Country"><Input value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} /></Field>
                <Field label="PIN / ZIP" error={err("addresses[0].pin")}><Input value={addr.pin} onChange={(e) => setAddr({ ...addr, pin: e.target.value })} data-testid="pf-addr-pin" /></Field>
              </div>
            </section>
          )}

          {(meta.custom_fields || []).length > 0 && (
            <section>
              <div className="text-sm font-semibold text-slate-700 mb-2">Additional Fields</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {meta.custom_fields.map((cf) => (
                  <Field key={cf.key} label={cf.label} required={cf.required} error={err(`custom_fields.${cf.key}`)}>
                    {cf.type === "select" ? (
                      <Sel value={f.custom_fields[cf.key]} onChange={(v) => setCF(cf.key, v)} options={(cf.options || []).map((o) => ({ value: o, label: o }))} />
                    ) : cf.type === "boolean" ? (
                      <Sel value={f.custom_fields[cf.key] ? "yes" : "no"} onChange={(v) => setCF(cf.key, v === "yes")} options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
                    ) : (
                      <Input type={cf.type === "number" ? "number" : cf.type === "date" ? "date" : "text"} value={f.custom_fields[cf.key] ?? ""}
                        onChange={(e) => setCF(cf.key, e.target.value)} />
                    )}
                  </Field>
                ))}
              </div>
            </section>
          )}

          <section>
            <Field label="Notes"><Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
          </section>

          {dups && (
            <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm" data-testid="pf-duplicates">
              <div className="flex items-center gap-2 font-semibold text-amber-800"><AlertTriangle className="w-4 h-4" /> Possible duplicate</div>
              <ul className="mt-1 text-xs text-amber-900 list-disc pl-5">
                {dups.map((d) => <li key={d.id}><span className="mono">{d.code}</span> {d.name} — matched on {d.matched_on.join(", ")}</li>)}
              </ul>
              {can("approve") ? (
                <div className="mt-2 flex gap-2 items-end">
                  <Field label="Reason to authorize a duplicate" className="flex-1">
                    <Input value={dupReason} onChange={(e) => setDupReason(e.target.value)} placeholder="e.g. separate GST branch" />
                  </Field>
                  <Button size="sm" variant="outline" disabled={!dupReason.trim() || busy} onClick={() => save(true)}>Create anyway</Button>
                </div>
              ) : <div className="text-xs text-amber-800 mt-1">Only an approver can authorize a duplicate record.</div>}
            </div>
          )}
        </div>
        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={busy} onClick={() => save(false)} data-testid="pf-save-btn">
            {editing ? "Save Changes" : "Create Draft"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
